package com.shadowings.gero.presentation.screen.characters

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DevicePreviews
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.face_cry
import gero.composeapp.generated.resources.face_doubt
import gero.composeapp.generated.resources.face_happy
import gero.composeapp.generated.resources.face_idle
import gero.composeapp.generated.resources.face_perfect
import gero.composeapp.generated.resources.face_sad
import gero.composeapp.generated.resources.face_serious
import gero.composeapp.generated.resources.face_sleeping
import gero.composeapp.generated.resources.face_surprised
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

// Alyn only renders a distinct expression for these — the rest (Idle, Sleeping, Doubt, AllBlack)
// fall through to the same default look in AlynAvatar's `when (face)`.
val previewableFaces: List<Faces> = listOf(
    Faces.Idle,
    Faces.Happy,
    Faces.Perfect,
    Faces.Sad,
    Faces.Cry,
    Faces.Surprised,
    Faces.Serious,
)

fun Faces.labelRes(): StringResource = when (this) {
    Faces.Idle -> Res.string.face_idle
    Faces.Happy -> Res.string.face_happy
    Faces.Perfect -> Res.string.face_perfect
    Faces.Sad -> Res.string.face_sad
    Faces.Cry -> Res.string.face_cry
    Faces.Surprised -> Res.string.face_surprised
    Faces.Serious -> Res.string.face_serious
    Faces.Sleeping -> Res.string.face_sleeping
    Faces.Doubt -> Res.string.face_doubt
    Faces.AllBlack -> Res.string.face_idle
}

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun FaceMenuPreview() {
    AppTheme {
        Surface {
            FaceMenu(
                selectedFace = Faces.Happy,
                onFaceSelected = {}
            )
        }
    }
}

@Composable
fun FaceMenu(
    selectedFace: Faces,
    onFaceSelected: (Faces) -> Unit,
    modifier: Modifier = Modifier,
    faces: List<Faces> = previewableFaces,
    labelRes: (Faces) -> StringResource = { it.labelRes() },
) {
    var expanded by remember { mutableStateOf(false) }
    val hapticFeedback = LocalHapticFeedback.current

    Box(modifier = modifier) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(10.dp))
                .background(MaterialTheme.colorScheme.surfaceVariant)
                .border(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f), RoundedCornerShape(10.dp))
                .semantics { testTag = "face_menu_trigger" }
                .clickable {
                    hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                    expanded = true
                }
                .padding(horizontal = 12.dp, vertical = 10.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = stringResource(labelRes(selectedFace)),
                style = MaterialTheme.typography.labelLarge
            )
            Text(
                text = "▾",
                style = MaterialTheme.typography.labelLarge
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false }
        ) {
            faces.forEach { face ->
                DropdownMenuItem(
                    text = { Text(stringResource(labelRes(face))) },
                    onClick = {
                        hapticFeedback.performHapticFeedback(HapticFeedbackType.LongPress)
                        onFaceSelected(face)
                        expanded = false
                    },
                    modifier = Modifier.semantics { testTag = "face_menu_item_${face.name.lowercase()}" }
                )
            }
        }
    }
}
